import Actions from 'flow/actions';
import {
    saveShowIncomingVideo,
    saveShowLocalVideoInGrid,
    saveVideoBackgroundPreferences,
} from 'flow/utils/browser-utils';

export const setVideoWebSocketAction = (webSocket) => ({
    type: Actions.VIDEO_SET_WEBSOCKET,
    webSocket,
});

export const setVideoJoinRoomPromiseAction = (promise) => ({
    type: Actions.VIDEO_SET_JOIN_ROOM_PROMISE,
    promise,
});

export const setVideoSenderPrimaryPeerAction = (primaryPeer) => ({
    type: Actions.VIDEO_SET_VIDEO_SENDER_PRIMARY_PEER,
    primaryPeer,
});

export const setVideoSenderSecondaryPeerAction = (secondaryPeer) => ({
    type: Actions.VIDEO_SET_VIDEO_SENDER_SECONDARY_PEER,
    secondaryPeer,
});

export const setVideoSenderStreamAction = (stream) => ({
    type: Actions.VIDEO_SET_VIDEO_SENDER_STREAM,
    stream,
});

export const setVideoSecondarySenderStreamAction = (secondaryStream) => ({
    type: Actions.VIDEO_SET_VIDEO_SECONDARY_SENDER_STREAM,
    secondaryStream,
});

export const setVideoSenderVideoActiveStatusAction = (videoActive) => ({
    type: Actions.VIDEO_SET_SENDER_VIDEO_ACTIVE_STATUS,
    videoActive,
});

export const setVideoSenderScreenShareActiveAction = (screenShareActive) => ({
    type: Actions.VIDEO_SET_SENDER_SCREEN_SHARE_ACTIVE,
    screenShareActive,
});

export const setVideoSenderScreenStreamAction = (screenStream) => ({
    type: Actions.VIDEO_SET_SENDER_SCREEN_STREAM,
    screenStream,
});

// The shared screen's true aspect ratio (w/h), measured from the on-stage
// video once its metadata loads; null when no screen is shared. The layout
// engine sizes the stage to this instead of assuming landscape.
export const setScreenShareAspectAction = (aspect) => ({
    type: Actions.VIDEO_SET_SCREEN_SHARE_ASPECT,
    aspect,
});

export const setVideoNewMeetingVideoMemberAction = (memberName) => ({
    type: Actions.VIDEO_ADD_VIDEO_MEMBER,
    memberName,
});

const setVideoShowSenderInGridStatus = (shouldShowInGrid) => ({
    type: Actions.VIDEO_SET_SHOW_LOCAL_VIDEO_IN_GRID,
    shouldShowInGrid,
});

export const saveShowSenderInGridStatus = (shouldShowInGrid) => (dispatch) => {
    saveShowLocalVideoInGrid(shouldShowInGrid);
    dispatch(setVideoShowSenderInGridStatus(shouldShowInGrid));
};

const setShowIncomingVideoAction = (showIncomingVideo) => ({
    type: Actions.VIDEO_SET_SHOW_INCOMING_VIDEO,
    showIncomingVideo,
});

export const saveShowIncomingVideoStatus =
    (showIncomingVideo) => (dispatch) => {
        saveShowIncomingVideo(showIncomingVideo);
        dispatch(setShowIncomingVideoAction(showIncomingVideo));
    };

export const setVideoShowScreenShareInGridStatusAction = (
    shouldShowScreenShareInGrid,
) => ({
    type: Actions.VIDEO_SET_SHOW_SCREEN_SHARE_IN_GRID,
    shouldShowScreenShareInGrid,
});

export const pinFeedAction = (feedName) => ({
    type: Actions.VIDEO_PIN_FEED,
    feedName,
});

export const unpinFeedAction = (feedName) => ({
    type: Actions.VIDEO_UNPIN_FEED,
    feedName,
});

export const clearPinnedFeedsAction = () => ({
    type: Actions.VIDEO_CLEAR_PINNED_FEEDS,
});

export const togglePinFeedAction = (feedName) => (dispatch, getState) => {
    const pinnedFeedNames = getState().video.get('pinnedFeedNames');
    if (pinnedFeedNames && pinnedFeedNames.has(feedName)) {
        dispatch(unpinFeedAction(feedName));
    } else {
        dispatch(pinFeedAction(feedName));
    }
};

export const setVideoMemberPeerSetupAttemptsAction = (
    memberName,
    setupAttempts,
) => ({
    type: Actions.VIDEO_SET_VIDEO_MEMBER_SETUP_ATTEMPTS,
    memberName,
    setupAttempts,
});

export const removeVideoMemberAction = (memberName) => ({
    type: Actions.VIDEO_REMOVE_VIDEO_MEMBER,
    memberName,
});

export const setVideoMembersEmptyAction = () => ({
    type: Actions.VIDEO_CLEAR_VIDEO_MEMBERS,
});

export const setVideoMemberPrimaryPeerAction = (
    memberName,
    memberPrimaryPeer,
) => ({
    type: Actions.VIDEO_SET_MEMBER_PRIMARY_PEER,
    memberName,
    memberPrimaryPeer,
});

export const setVideoMemberSecondaryPeerAction = (
    memberName,
    memberSecondaryPeer,
) => ({
    type: Actions.VIDEO_SET_MEMBER_SECONDARY_PEER,
    memberName,
    memberSecondaryPeer,
});

export const setVideoMemberTrackActiveStatusAction = (
    memberName,
    trackType,
    active,
) => ({
    type: Actions.VIDEO_SET_MEMBER_TRACK_ACTIVE_STATUS,
    memberName,
    trackType,
    active,
});

export const setVideoActivityListAction = (activityList) => ({
    type: Actions.VIDEO_SET_NEW_ACTIVITY,
    activityList,
});

export const setSpeakingMembersAction = (speakingMembers) => ({
    type: Actions.VIDEO_SET_SPEAKING_MEMBERS,
    speakingMembers,
});

export const setOwnUserIsSpeakingAction = (ownUserIsSpeaking) => ({
    type: Actions.VIDEO_SET_OWN_USER_IS_SPEAKING,
    ownUserIsSpeaking,
});

// Everyone currently asking to join (VID-76), as their join names. The server
// re-sends the whole list on every change rather than deltas, so two people
// allowing or denying at once converge instead of racing.
export const setWaitingParticipantsAction = (waitingParticipants) => ({
    type: Actions.VIDEO_SET_WAITING_PARTICIPANTS,
    waitingParticipants,
});

export const setOwnWaitingRoomStandingAction = (
    waitingRoomStanding,
    cause = null,
) => ({
    type: Actions.VIDEO_SET_OWN_WAITING_ROOM_STANDING,
    waitingRoomStanding,
    cause,
});

export const setVideoConnectionStateAction = (connectionState) => ({
    type: Actions.VIDEO_SET_CONNECTION_STATE,
    connectionState,
});

export const setVideoSenderReconnectionTokenAction = (reconnectionToken) => ({
    type: Actions.VIDEO_SET_SENDER_RECONNECTION_TOKEN,
    reconnectionToken,
});

// Internal action creators (not exported, used internally)
const setVideoBackgroundBlurAmountActionInternal = (blurAmount) => ({
    type: Actions.VIDEO_SET_BACKGROUND_BLUR_AMOUNT,
    blurAmount,
});

const setVideoBackgroundImageActionInternal = (imageUrl) => ({
    type: Actions.VIDEO_SET_BACKGROUND_IMAGE,
    imageUrl,
});

const setVideoBackgroundModeActionInternal = (mode) => ({
    type: Actions.VIDEO_SET_BACKGROUND_MODE,
    mode,
});

// Helper to save current background preferences to localStorage
// Only persist mode and imageUrl - blurAmount uses default value
const saveCurrentBackgroundPreferences = (getState) => {
    const state = getState();
    const preferences = {
        mode: state.video.getIn(['backgroundEffect', 'mode']),
        imageUrl: state.video.getIn(['backgroundEffect', 'imageUrl']),
    };
    saveVideoBackgroundPreferences(preferences);
};

// Public action creators that both dispatch and persist
export const setVideoBackgroundBlurAmountAction =
    (blurAmount) => (dispatch, getState) => {
        dispatch(setVideoBackgroundBlurAmountActionInternal(blurAmount));
        saveCurrentBackgroundPreferences(getState);
    };

export const setVideoBackgroundImageAction =
    (imageUrl) => (dispatch, getState) => {
        dispatch(setVideoBackgroundImageActionInternal(imageUrl));
        saveCurrentBackgroundPreferences(getState);
    };

export const setVideoBackgroundModeAction = (mode) => (dispatch, getState) => {
    dispatch(setVideoBackgroundModeActionInternal(mode));
    saveCurrentBackgroundPreferences(getState);
};
